import {
  AssignMaterialsRequest,
  AssignNonModelledElementsEditsRequest,
  AssignNonModelledElementsRequest,
  AssignRebarEstimationEditsRequest,
  AssignRebarEstimationsRequest,
  AssignScopesRequest,
  AutoAssignMaterialsRequest,
  Category,
  CategoryDto,
  Component,

  CreateModelVersionRequest,
  DeleteModelRequest,
  Element,
  FileParameter,
  Material,
  Model,
  ModelsClient,
  ModelScopesResponse,
  ModelSplitRequest,
  ProjectModelDto,
  RemoveMaterialsRequest,
  RemoveSupplementalsRequest,
  UpsertModel,
  UpdateModelVersionRequest as ApiUpdateModelVersionRequest,
  ModelUsageInfo,
  ModelTypeDto,
} from "@api/apiClient";
import axios from "@api/axiosInstance";

import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

export const modelsClient = new ModelsClient(undefined, axios);

export const useModel = (modelId: string | undefined, options?: any) => {
  return useQuery<Model | null>({
    ...options,
    queryKey: ["model", modelId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModel(modelId) : new Promise((resolve) => resolve(null)),
  });
};

export const useProjectModel = (modelId: string | undefined, dataVersion: number | undefined, options?: any) => {
  return useQuery<ProjectModelDto | null>({
    ...options,
    queryKey: ["projectModel", modelId, dataVersion],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getProjectModel(modelId) : Promise.resolve(null),
  });
};

export const useCategories = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Category[] | null>({
    ...options,
    queryKey: ["categories", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelCategories(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useElements = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Element[] | null>({
    ...options,
    queryKey: ["elements", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelElements(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useComponents = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Element[] | null>({
    ...options,
    queryKey: ["components", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelComponents(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useModelExtendedData = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<{ [key: string]: { [key: string]: any; }; } | null>({
    ...options,
    queryKey: ["modelExtendedData", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelExtendedData(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useMaterials = (modelId: string | undefined, scenarioId: string | undefined, options?: any) => {
  return useQuery<Material[] | null>({
    ...options,
    queryKey: ["materials", modelId, scenarioId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelMaterials(modelId!, scenarioId) : new Promise((resolve) => resolve(null)),
  });
};

export const useModelScopes = (modelId: string | undefined, options?: any) => {
  return useQuery<ModelScopesResponse | null>({
    ...options,
    queryKey: ["modelScopes", modelId],
    initialData: null,
    queryFn: async () => modelId ? modelsClient.getModelScopes(modelId!) : new Promise((resolve) => resolve(null)),
  });
};

export const useCreateModelImage = (modelId: string | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (fileParameter: FileParameter) => modelsClient.uploadModelImage(modelId!, fileParameter),
  });
};

export const useUpdateModel = (modelId: string | undefined, options?: any) => {
  const userOnSuccess = options?.onSuccess as ((data: Model) => void | Promise<void>) | undefined;
  return useMutation<Model, unknown, UpsertModel>({
    mutationFn: (upsertModel: UpsertModel) => modelsClient.updateModel(modelId!, upsertModel),
    onSuccess: async (model: Model) => {
      queryClient.setQueryData(["model", modelId], model);
      await userOnSuccess?.(model);
    },
  });
};

export const useAssignMaterials = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignMaterialsRequest: AssignMaterialsRequest) => modelsClient.assignMaterials(modleId!, assignMaterialsRequest),
  });
};

export const useAutoAssignMaterials = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (autoAssignMaterialsRequest: AutoAssignMaterialsRequest) => modelsClient.autoAssignMaterials(modleId!, autoAssignMaterialsRequest),
  });
};

export const useAssignRebarEstimations = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignRebarEstimationsRequest: AssignRebarEstimationsRequest) => modelsClient.assignRebarEstimations(modleId!, assignRebarEstimationsRequest),
  });
};

export const useAssignRebarEstimationEdits = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignRebarEstimationEditsRequest: AssignRebarEstimationEditsRequest) => modelsClient.assignRebarEstimationEdits(modleId!, assignRebarEstimationEditsRequest),
  });
};

export const useAssignNonModelledElements = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignNonModelledElementsRequest: AssignNonModelledElementsRequest) => modelsClient.assignNonModelledElements(modleId!, assignNonModelledElementsRequest),
  });
};

export const useAssignNonModelledElementsEdits = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignNonModelledElementsEditsRequest: AssignNonModelledElementsEditsRequest) => modelsClient.assignNonModelledElementsEdits(modleId!, assignNonModelledElementsEditsRequest),
  });
};

export const useAssignScopes = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (assignScopesRequest: AssignScopesRequest) => modelsClient.assignScopes(modleId!, assignScopesRequest),
  });
};

export const useRemoveMaterials = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (removeMaterialsRequest: RemoveMaterialsRequest) => modelsClient.removeMaterials(modleId!, removeMaterialsRequest),
  });
};

export const useRemoveSupplementals = (modleId: string | null | undefined, options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (removeSupplementalsRequest: RemoveSupplementalsRequest) => modelsClient.removeSupplementals(modleId!, removeSupplementalsRequest),
  });
};

export const useSplitModel = (options?: any) => {
  return useMutation<ModelSplitRequest, unknown, ModelSplitRequest>({
    ...options,
    mutationFn: (value: ModelSplitRequest) => modelsClient.splitModel(value),
  });
};

export const useCreateModelVersion = (options?: any) => {
  return useMutation<void, unknown, CreateModelVersionRequest>({
    ...options,
    mutationFn: (request: CreateModelVersionRequest) => modelsClient.createModelVersion(request),
  });
};

export const useDeleteModel = (options?: any) => {
  return useMutation<void, unknown, DeleteModelRequest>({
    ...options,
    mutationFn: (request: DeleteModelRequest) => modelsClient.deleteModel(request),
  });
};

export const useModelUsageInfo = (modelId: string | undefined, options?: any) => {
  return useQuery<ModelUsageInfo | null>({
    ...options,
    queryKey: ["modelUsageInfo", modelId],
    initialData: null,
    enabled: !!modelId,
    queryFn: async () => {
      if (!modelId) return null;
      return modelsClient.getModelUsageInfo(modelId);
    },
  });
};

export const useUpdateModelVersion = (options?: any) => {
  return useMutation<void, Error, ApiUpdateModelVersionRequest>({
    ...options,
    mutationFn: (request: ApiUpdateModelVersionRequest) => modelsClient.updateModelVersion(request.modelId!, request),
  });
};

export const useModelFamilies = (options?: any) => {
  return useQuery<string[]>({
      ...options,
      queryKey: ["modelFamilies"],
      queryFn: async () => modelsClient.getModelFamilies(),
    });
};

export const useModelTypes = (options?: any) => {
  return useQuery<ModelTypeDto[]>({
    ...options,
    queryKey: ["modelTypes"],
    queryFn: async () => modelsClient.getModelTypes(),
  });
};